import { ChevronLeft } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Information We Collect",
      content:
        "We collect information you provide directly, including account details (name, email), health data (weight, height, age, dietary preferences), meal photos and nutritional logs, fitness tracker data when connected, and usage patterns within the app.",
    },
    {
      title: "2. How We Use Your Information",
      content:
        "Your information is used to provide personalized health insights and recommendations, calculate your Bite Balance score and nutritional metrics, sync with connected fitness devices, improve our AI meal recognition technology, and send relevant notifications and updates.",
    },
    {
      title: "3. Data Storage & Security",
      content:
        "We employ industry-standard encryption (AES-256) for data at rest and in transit. Your health data is stored on secure, HIPAA-compliant servers. We implement regular security audits and penetration testing to ensure your data remains protected.",
    },
    {
      title: "4. Data Sharing",
      content:
        "We do not sell your personal health data to third parties. We may share anonymized, aggregated data for research purposes. Third-party integrations (like Fitbit) receive only the minimum data necessary for functionality.",
    },
    {
      title: "5. Your Rights",
      content:
        "You have the right to access, correct, or delete your personal data at any time. You can export your data in a portable format. You may opt out of non-essential data collection through app settings.",
    },
    {
      title: "6. Cookies & Tracking",
      content:
        "We use essential cookies to maintain your session and preferences. Analytics cookies help us understand app usage patterns. You can manage cookie preferences in your device settings.",
    },
    {
      title: "7. Children's Privacy",
      content:
        "Bite Balance is not intended for users under 13 years of age. We do not knowingly collect personal information from children. If we discover such data has been collected, it will be promptly deleted.",
    },
    {
      title: "8. International Data Transfers",
      content:
        "Your data may be processed in countries other than your own. We ensure appropriate safeguards are in place for international transfers in compliance with applicable data protection laws.",
    },
    {
      title: "9. Data Retention",
      content:
        "We retain your data for as long as your account is active. Upon account deletion, your personal data is permanently removed within 30 days. Some anonymized data may be retained for analytical purposes.",
    },
    {
      title: "10. Contact Us",
      content:
        "For privacy-related inquiries or to exercise your data rights, contact our Data Protection Officer at privacy@bitebalance.com.",
    },
  ];

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center pt-2">
          <button
            onClick={() => navigate("/profile/about")}
            className="rounded-full p-2 transition-colors hover:bg-muted"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="ml-2">
            <h1 className="text-xl font-bold text-foreground">
              Privacy Policy
            </h1>
            <p className="text-xs text-muted-foreground">
              Last updated: December 2025
            </p>
          </div>
        </div>

        {/* Introduction */}
        <Card className="to-chart-1/5 rounded-2xl border-0 bg-gradient-to-br from-primary/5 p-5 shadow-sm">
          <p className="text-sm leading-relaxed text-muted-foreground">
            At Bite Balance, your privacy is our priority. This policy explains
            how we collect, use, and protect your personal information. We are
            committed to transparency and giving you control over your data.
          </p>
        </Card>

        {/* Privacy Sections */}
        <div className="space-y-3">
          {sections.map((section, index) => (
            <Card key={index} className="rounded-2xl border-0 p-4 shadow-sm">
              <h3 className="mb-2 font-semibold text-foreground">
                {section.title}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {section.content}
              </p>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          © 2025 Bite Balance. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
